/* =============================================================================
   VIEWS / MENU-PICKER-RENDER-LINE — Bestellzeilen-Render + Konfig-Modal
   =============================================================================
   S69 REF-4 Cut 2/3: aus menu-picker.js (1.354 LOC) extrahiert. Größter
   Render-Block in einem File — `renderLineConfigBody` allein ~300 LOC.

   Pure Render-Funktionen — kein `_state`-Zugriff, alle Daten als Argumente.
   Macht Test-Mocking trivial und entkoppelt das Render-Layer vom State-Modul.

   Enthält:
     - renderOrderLine(line, menu)          — Bon-Zeile im Warenkorb mit
                                              Mengen-Stepper + Edit/Remove
     - renderLineReceiptDetails(line)       — Textuelle Anpassungs-Auflistung
                                              (pure, nur line.* properties)
     - renderLineConfigBody(line, item,     — Mega-Block (~300 LOC) mit:
         menu, dietFilter)                    Bun/Patty/Dressing/Toppings/
                                              Patty-Swap/Extras/Sides/SideSauces/
                                              Sauces/Milk
     - renderDraftModal(draftLine, menu,    — Pop-Up vor Warenkorb-Add
         dietFilter)
     - renderModalFooter(line, item,        — Footer mit Mengen + Preis +
         missing)                             Action-Buttons (separat damit
                                              softRefresh nur den Footer
                                              ersetzen kann ohne Open-
                                              Animation neu zu triggern)

   Inline-onclick-Handler (mp*) zeigen auf window-Bindings — keine direkten
   Imports nötig (S66/S67/S68-bewährtes Pattern).
============================================================================ */

import { htmlEscape, fmtEUR } from '../utils/helpers.js';
import {
  dietTagBadges,
  findItemFromMenu,
  optionsByGroupFromMenu,
  addonsByGroupFromMenu,
  itemsByCategoryFromMenu,
  resolveExtrasGroup,
  sidesOf
} from './menu-picker-helpers.js';
import { rulesForDishGroup } from '../core/menu-configurator.js';

/** Bestellzeile im Warenkorb — kompakte "Bon-Ansicht": Mengen-Stepper,
 *  Name, Preis, Edit + Remove rechts. Darunter eine textuelle Auflistung
 *  der Anpassungen (Brötchen, Patty-Tausch, Streichungen, Extras, Beilagen,
 *  Soßen). Volle Editier-UI nur noch via Edit-Button (öffnet das Modal). */
export function renderOrderLine(line, menu) {
  const item = findItemFromMenu(menu, line.ref);
  if (!item) return '';
  return `
    <div class="mp-line mp-line-receipt" data-line="${line.lineId}">
      <div class="mp-line-head">
        <div class="mp-qty">
          <button type="button" class="mp-qty-btn" onclick="mpChangeQty('${line.lineId}', -1)" aria-label="Weniger">−</button>
          <span class="mp-qty-val">${line.qty}</span>
          <button type="button" class="mp-qty-btn" onclick="mpChangeQty('${line.lineId}', 1)" aria-label="Mehr">+</button>
        </div>
        <div class="mp-line-name">
          <span class="mp-line-title">${htmlEscape(line.name)}</span>
          ${dietTagBadges(item.dietaryTags)}
        </div>
        <div class="mp-line-price">${fmtEUR(line.lineTotal)}</div>
        <div class="mp-line-actions">
          <button type="button" class="mp-line-edit"
                  onclick="mpEditLine('${line.lineId}')"
                  aria-label="Bearbeiten" title="Anpassungen ändern">✎</button>
          <button type="button" class="mp-line-remove"
                  onclick="mpRemoveLine('${line.lineId}')"
                  aria-label="Entfernen" title="Position entfernen">✕</button>
        </div>
      </div>
      ${renderLineReceiptDetails(line)}
    </div>
  `;
}

/** Textuelle Anpassungs-Auflistung — eine Sub-Zeile pro relevantes Detail.
 *  Wird identisch im Customer-Warenkorb und im Admin-Detail-Modal genutzt
 *  (über die Helpers in utils/helpers.js). Liefert leeren String, wenn die
 *  Bestellzeile keine Anpassungen hat. */
export function renderLineReceiptDetails(line) {
  const parts = [];
  // S229 T2 — Warenkorb-Sub-Zeilen zeigen KEINE Einzelpreise mehr für
  // Unterkomponenten (Brötchen-Aufpreis, Beilagen); der Positionsgesamtpreis
  // (mp-line-price) reicht. Kassenbon + Mails bleiben davon unberührt.
  if (line.bun) {
    parts.push(`Brötchen: ${htmlEscape(line.bun.name)}`);
  }
  if (line.patty)    parts.push(`Patty: ${htmlEscape(line.patty.name)}`);
  if (line.dressing) parts.push(`Dressing: ${htmlEscape(line.dressing.name)}`);
  const removedAll = line.removedIngredients || [];
  const removedVisible = removedAll.filter(x => x !== '__patty__');
  if (removedVisible.length) {
    parts.push(`ohne: ${removedVisible.map(htmlEscape).join(', ')}`);
  }
  if (removedAll.includes('__patty__') && !line.patty) {
    parts.push(`<strong>ohne Patty</strong>`);
  }
  if ((line.extras || []).length) {
    parts.push(`Extras: ${line.extras.map(e => htmlEscape(e.name)).join(', ')}`);
  }
  // S229 T1 — alle Beilagen listen (Mehrfachauswahl); T2 — ohne Einzelpreise.
  // S231 — „Beilagen:"-Label statt „+ …" (konsistent mit ohne:/Extras:/Soßen:).
  const sidesArr = sidesOf(line);
  if (sidesArr.length) {
    let s = `Beilagen: ${sidesArr.map(x => htmlEscape(x.name)).join(', ')}`;
    if ((line.sideSauces || []).length) {
      s += ` mit ${line.sideSauces.map(e => htmlEscape(e.name)).join(', ')}`;
    }
    parts.push(s);
  }
  if ((line.sauces || []).length) {
    parts.push(`Soßen: ${line.sauces.map(e => htmlEscape(e.name)).join(', ')}`);
  }
  // S196 (Jonas) — Positions-Kommentar (Cockpit) als eigene, hervorgehobene Zeile.
  const noteRow = (line.note && String(line.note).trim())
    ? `<div class="mp-receipt-row mp-receipt-note">📝 ${htmlEscape(String(line.note).trim())}</div>`
    : '';
  if (parts.length === 0 && !noteRow) return '';
  return `
    <div class="mp-line-receipt-details">
      ${parts.map(p => `<div class="mp-receipt-row">· ${p}</div>`).join('')}
      ${noteRow}
    </div>
  `;
}

/** Body mit allen Konfigurations-Reihen. Wird identisch im Warenkorb
 *  (renderOrderLine) und im Konfigurations-Modal (renderDraftModal) genutzt. */
export function renderLineConfigBody(line, item, menu, dietFilter, opts) {
  const meta = item.meta || {};

  let bunBlock = '';
  if (meta.isBurger && meta.bunGroup) {
    let buns = optionsByGroupFromMenu(menu, meta.bunGroup);
    // S169 P3-Rework — im Deal-Kontext Brötchen auf rules.bun_whitelist beschränken
    // (Name-Match, tolerant). Guarded über opts → Normal-Bestellung unverändert.
    const wl = opts && opts.bunWhitelist;
    if (Array.isArray(wl) && wl.length) {
      const norm = s => String(s || '').toLowerCase().trim();
      const wlNorm = wl.map(norm).filter(Boolean);
      const filtered = buns.filter(b => wlNorm.some(w => norm(b.name).includes(w) || w.includes(norm(b.name))));
      if (filtered.length) buns = filtered;
    }
    bunBlock = `
      <div class="mp-row mp-row-bun">
        <span class="mp-row-label">Brötchen</span>
        <div class="mp-bun-list">
          ${buns.map(b => {
            const isSel = line.bun && line.bun.id === b.id;
            const extra = (b.meta && Number(b.meta.extraPrice)) || 0;
            return `
              <button type="button"
                class="mp-bun-chip ${isSel ? 'active' : ''}"
                onclick="mpSelectBun('${line.lineId}','${b.id}')">
                ${htmlEscape(b.name)}${extra > 0 ? ` <small>+${fmtEUR(extra)}</small>` : ''}
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  let pattyBlock = '';
  if (meta.isBowl && meta.requiresPatty && meta.pattyGroup) {
    const pattys = optionsByGroupFromMenu(menu, meta.pattyGroup);
    pattyBlock = `
      <div class="mp-row mp-row-required ${!line.patty ? 'mp-row-missing' : ''}">
        <span class="mp-row-label">Patty *</span>
        <select class="mp-select" onchange="mpSelectPatty('${line.lineId}', this.value)">
          <option value="">— bitte wählen —</option>
          ${pattys.map(p => `<option value="${p.id}" ${line.patty && line.patty.id === p.id ? 'selected' : ''}>${htmlEscape(p.name)}</option>`).join('')}
        </select>
      </div>
    `;
  }

  let dressingBlock = '';
  if (meta.isBowl && meta.requiresDressing && meta.dressingGroup) {
    const dressings = optionsByGroupFromMenu(menu, meta.dressingGroup);
    dressingBlock = `
      <div class="mp-row mp-row-required ${!line.dressing ? 'mp-row-missing' : ''}">
        <span class="mp-row-label">Dressing *</span>
        <select class="mp-select" onchange="mpSelectDressing('${line.lineId}', this.value)">
          <option value="">— bitte wählen —</option>
          ${dressings.map(d => `<option value="${d.id}" ${line.dressing && line.dressing.id === d.id ? 'selected' : ''}>${htmlEscape(d.name)}</option>`).join('')}
        </select>
      </div>
    `;
  }

  // Burger-Patty als eigene Reihe (über den anderen Zutaten). Klick auf
  // den Patty-Chip toggelt "Patty streichen" und öffnet das Swap-Panel
  // automatisch, damit der Gast direkt eine Alternative wählen kann.
  // Daneben ein dezenter "Patty tauschen"-Link — auch ohne Streichen
  // erreichbar. Kein ⇅-Icon mehr.
  let burgerPattyBlock = '';
  if (meta.isBurger && meta.allowPattySwap) {
    const removedSet0 = new Set(line.removedIngredients || []);
    const isStruck = removedSet0.has('__patty__');
    const pattyName = (line.patty && line.patty.name) ||
                      meta.defaultPatty ||
                      (Array.isArray(item.ingredients) ? item.ingredients[0] : '') ||
                      '';
    burgerPattyBlock = `
      <div class="mp-row mp-row-patty">
        <span class="mp-row-label">Patty</span>
        <div class="mp-patty-display">
          <button type="button"
                  class="mp-patty-chip ${isStruck ? 'struck' : ''}"
                  onclick="mpTogglePatty('${line.lineId}')"
                  title="${isStruck ? 'Patty wieder hinzufügen' : 'Patty streichen'}">
            ${htmlEscape(pattyName)}
          </button>
          <button type="button" class="mp-patty-swap-link"
                  onclick="mpOpenPattySwap('${line.lineId}')">
            ${isStruck ? 'Ersatz wählen' : 'Patty tauschen'}
          </button>
        </div>
      </div>
    `;
  }

  // Zutaten ohne das Patty (das hat seine eigene Reihe darüber). Bei
  // Burgern überspringen wir Index 0; sonst werden alle Zutaten gerendert.
  let ingBlock = '';
  if (Array.isArray(item.ingredients) && item.ingredients.length > 0) {
    const startIdx = (meta.isBurger && meta.allowPattySwap) ? 1 : 0;
    const remaining = item.ingredients.slice(startIdx);
    if (remaining.length > 0) {
      const removedSet = new Set(line.removedIngredients || []);
      ingBlock = `
        <div class="mp-row mp-row-ing">
          <span class="mp-row-label">${meta.isBurger ? 'Toppings' : 'Zutaten'}</span>
          <div class="mp-ing-list">
            ${remaining.map(ing => {
              const isStruck = removedSet.has(ing);
              return `
                <span class="mp-ing ${isStruck ? 'struck' : ''}"
                      onclick="mpStrikeIngredient('${line.lineId}','${htmlEscape(ing).replace(/'/g, '&#39;')}')">${htmlEscape(ing)}</span>
              `;
            }).join('<span class="mp-ing-sep">·</span>')}
          </div>
        </div>
      `;
    }
  }

  // Patty-Swap-Picker (inline, wird durch mpOpenPattySwap aktiviert)
  let pattySwapBlock = '';
  if (meta.isBurger && meta.allowPattySwap && line._pattySwapOpen) {
    const pattys = optionsByGroupFromMenu(menu, meta.pattyGroup);
    pattySwapBlock = `
      <div class="mp-patty-swap-panel">
        <div class="mp-patty-swap-head">Patty tauschen:</div>
        <div class="mp-patty-swap-grid">
          ${pattys.map(p => {
            const isCurrent = (line.patty && line.patty.id === p.id) ||
                              (!line.patty && p.name === meta.defaultPatty);
            return `
              <button type="button"
                class="mp-patty-swap-chip ${isCurrent ? 'active' : ''}"
                onclick="mpSwapPatty('${line.lineId}','${p.id}')">${htmlEscape(p.name)}</button>
            `;
          }).join('')}
          <button type="button" class="mp-patty-swap-cancel"
            onclick="mpClosePattySwap('${line.lineId}')">Abbrechen</button>
        </div>
      </div>
    `;
  }

  // Extras
  // F4 (Session 20 Funktionstest): Salate sollen dieselben Extras wie Bowls
  // bekommen — explizit OHNE Patty und OHNE Dressing (das sind eigene Blöcke
  // mit meta.isBowl-Check, also automatisch ausgeschlossen). Wenn ein Salat
  // keine eigene extrasGroup in den DB-Stammdaten hat, leihen wir uns die
  // Bowl-Extras-Group (kein DB-Migration nötig). Falls Salate später eigene
  // Extras-Groups bekommen, greift weiterhin die direkte meta.extrasGroup.
  const extrasGroup = resolveExtrasGroup(menu, item);
  const extrasOpts = extrasGroup ? addonsByGroupFromMenu(menu, extrasGroup, 'extra') : [];

  // S193 — Extras-Regeln kommen vom GERICHT (meta.optionSets); Fallback Heuristik
  // (extras = Mehrfach optional) für un-migrierte Gerichte. Steuert Pflicht-Stern,
  // mp-row-required/-missing + Hinweis nur-eine/min/max.
  const extrasRules = rulesForDishGroup(meta, extrasGroup, { role: 'extras', label: 'Extras' });
  const extrasCount = (line.extras || []).length;
  const extrasMin = extrasRules.min != null ? extrasRules.min : (extrasRules.required ? 1 : 0);
  const extrasRequired = extrasMin >= 1;
  const extrasMissing = extrasCount < extrasMin;
  // S193-Fix3 — Hinweis multi-bewusst: bei Einfachauswahl (multi:false) NUR „nur eine"
  // (min/max sind da sinnlos/verwirrend); bei Mehrfach min/max + „mehrere möglich".
  let extrasHint = '';
  if (!extrasRules.multi) {
    extrasHint = 'nur eine';
  } else if (extrasRules.min != null && extrasRules.max != null && extrasRules.min === extrasRules.max) {
    extrasHint = `genau ${extrasRules.min}`;
  } else {
    const parts = [];
    if (extrasRules.min != null && extrasRules.min > 0) parts.push(`min. ${extrasRules.min}`);
    if (extrasRules.max != null) parts.push(`max. ${extrasRules.max}`);
    if (parts.length) parts.unshift('mehrere möglich');
    extrasHint = parts.join(' · ');
  }

  const extrasBlock = (extrasOpts.length > 0) ? `
    <div class="mp-row mp-row-addons ${extrasRequired ? 'mp-row-required' : ''} ${extrasMissing ? 'mp-row-missing' : ''}">
      <span class="mp-row-label">Extras${extrasRequired ? ' *' : ''}${extrasHint ? ` <small>(${extrasHint})</small>` : ''}</span>
      <div class="mp-addon-list">
        ${extrasOpts.map(e => {
          const selected = (line.extras || []).some(x => x.id === e.id);
          return `
            <button type="button"
              class="mp-addon-chip ${selected ? 'active' : ''}"
              onclick="mpToggleExtra('${line.lineId}','${e.id}')">
              ${htmlEscape(e.name)} <small>+${fmtEUR(e.price)}</small>
            </button>
          `;
        }).join('')}
      </div>
    </div>
  ` : '';

  // Beilagen-Up-Sell bei Burger + Bowl + Salat: Beilagen werden als Sub-Optionen
  // an DIESELBE Bestellzeile gehängt (line.sides[]). S229 T1 — Checkbox-Semantik:
  // mehrere VERSCHIEDENE Beilagen wählbar (je Beilage max 1×), jede fließt in den
  // Positionspreis. Sobald mindestens eine Beilage gewählt ist, erscheint darunter
  // die Soßen-Reihe (eine Dip-Liste pro Position). Damit hat die Combo
  // "Cheeseburger + Pommes + Zwiebelringe + Ketchup" eine einzige Bestellposition
  // statt mehrerer. Nochmaliges Klicken auf einen aktiven Chip entfernt genau diese
  // Beilage. Salate seit Session 10 mit drin.
  let sidesUpsellBlock = '';
  let sideSauceBlock = '';
  // S169 P3-Rework — im Deal-Modus entfällt der normale Beilagen-Up-Sell: die
  // Beilage kommt aus dem „Aktions-Menü" (deal_addons). Toppings/Extras bleiben.
  const _dealMode = !!(opts && opts.dealMode);
  if (!_dealMode && (meta.isBurger || meta.isBowl || meta.isSalad)) {
    const sides = itemsByCategoryFromMenu(menu, 'Beilagen', dietFilter);
    if (sides.length > 0) {
      sidesUpsellBlock = `
        <div class="mp-row mp-row-addons">
          <span class="mp-row-label">Beilage dazu?</span>
          <div class="mp-addon-list">
            ${sides.map(s => {
              // S229 T1 — mehrere Chips gleichzeitig aktiv (Checkbox-Semantik).
              const isSel = sidesOf(line).some(x => x.id === s.id);
              return `
                <button type="button"
                  class="mp-addon-chip mp-addon-side ${isSel ? 'active' : ''}"
                  onclick="mpToggleSide('${line.lineId}','${s.id}')"
                  title="${isSel ? 'Beilage entfernen' : 'Beilage zu deinem ' + htmlEscape(item.name) + ' hinzufügen'}">
                  ${isSel ? '✓' : '+'} ${htmlEscape(s.name)} <small>${isSel ? '' : '+'}${fmtEUR(s.price)}</small>
                </button>
              `;
            }).join('')}
          </div>
        </div>
      `;
    }

    // sideSauceBlock entfällt seit Session 20 / F1 (Jonas-Funktionstest):
    // Der Standard-Soßen-Block weiter unten zeigt die Soßen bei Burger/Bowl/Salat
    // sowieso immer an — eine zweite Soßen-Reihe als Sub-Block der Beilage wäre
    // Duplikat. Soße zur Beilage wird intern im Standard-Block über line.sauces
    // gepflegt (Mayo zum Burger == Mayo zur Pommes; semantisch reicht 1 Soße).
  }

  // Soßen-Reihe — Session 20 / Phase 16.3 Upsell-Erweiterung:
  // Vorher: nur sichtbar bei Beilagen-Einzelposition (item.itemType==='side')
  //         oder als Sub-Block, wenn eine Beilage zum Burger gewählt war.
  // Jetzt:  Soßen sind STANDARDMÄSSIG zur Auswahl verfügbar — auch bei
  //         Burger/Bowl/Salat/Kinder ohne gewählte Beilage. Der Gast kann
  //         z.B. einen Burger + Mayo bestellen, ohne erst eine Pommes nehmen
  //         zu müssen. Den separaten sideSauceBlock haben wir in F1
  //         (Session 20 Funktionstest) entfernt: eine Soßen-Reihe reicht.
  const saucesOpts = addonsByGroupFromMenu(menu, null, 'sauce');
  const showStandardSauces =
        !_dealMode &&                       // S169 P3 — Deal: Soßen inkl. (kein +0,80-Upsell)
        saucesOpts.length > 0 && (
          item.itemType === 'side'
          || meta.isBurger
          || meta.isBowl
          || meta.isSalad
          || item.itemType === 'kids'
          || (item.category && /kinder/i.test(item.category))
        );
  const saucesBlock = showStandardSauces ? `
    <div class="mp-row mp-row-addons mp-row-sauces">
      <span class="mp-row-label">
        Soßen dazu? <small>(+0,80 pro Stück)</small>
      </span>
      <div class="mp-addon-list">
        ${saucesOpts.map(e => {
          const selected = (line.sauces || []).some(x => x.id === e.id);
          return `
            <button type="button"
              class="mp-addon-chip ${selected ? 'active' : ''}"
              onclick="mpToggleSauce('${line.lineId}','${e.id}')"
              title="${selected ? 'Soße entfernen' : 'Soße zu deinem ' + htmlEscape(item.name) + ' hinzufügen'}">
              ${selected ? '✓ ' : '+ '}${htmlEscape(e.name)}
            </button>
          `;
        }).join('')}
      </div>
    </div>
  ` : '';

  // — Milch-Auswahl (F7b, Session 20) ————————————————————————
  // Bei Milchgetränken (Latte, Cappuccino, Kakao, Milchkaffee etc.) muss das
  // Theken-Personal wissen welche Milch — sonst halten wir den Gast unnötig
  // auf. Erkennung per Heuristik am Item-Namen, damit ohne DB-Migration
  // ausrollbar. Wenn Items später ein meta.allowMilkChoice-Flag bekommen,
  // greift das hier zusätzlich.
  const milkOptions = [
    { id: 'milk_kuh',    name: 'Kuhmilch' },
    { id: 'milk_hafer',  name: 'Hafermilch' },
    { id: 'milk_mandel', name: 'Mandelmilch' }
  ];
  let milkBlock = '';
  const itemName = String(item.name || '').toLowerCase();
  const isMilkDrink = (meta && meta.allowMilkChoice) ||
                      /latte|cappuccino|kakao|milchkaffee|chai|matcha|flat white|cortado|macchiato/i.test(itemName);
  if (isMilkDrink) {
    milkBlock = `
      <div class="mp-row mp-row-addons mp-row-milk">
        <span class="mp-row-label">Milch *</span>
        <div class="mp-addon-list">
          ${milkOptions.map(m => {
            const selected = line.milk && line.milk.id === m.id;
            return `
              <button type="button"
                class="mp-addon-chip ${selected ? 'active' : ''}"
                onclick="mpSelectMilk('${line.lineId}','${m.id}')">
                ${selected ? '✓ ' : ''}${htmlEscape(m.name)}
              </button>
            `;
          }).join('')}
        </div>
      </div>
    `;
  }

  return `
    ${bunBlock}
    ${pattyBlock}
    ${burgerPattyBlock}
    ${dressingBlock}
    ${milkBlock}
    ${ingBlock}
    ${pattySwapBlock}
    ${extrasBlock}
    ${sidesUpsellBlock}
    ${sideSauceBlock}
    ${saucesBlock}
  `;
}

/** S196 (Jonas) — Positions-Kommentar für Küche/Theke. NUR Cockpit (Operator):
 *  im Anpassen-Konfigurator gepflegt → läuft beim Bestellen mit der Runde an
 *  KDS/Theke (order_items.notes). oninput spiegelt live in den Line-State, damit
 *  ein softRefresh (andere Option gewählt) den Text nicht verliert. */
export function renderKitchenNoteBlock(line) {
  return `
    <div class="mp-row mp-row-note">
      <span class="mp-row-label">Kommentar für Küche/Theke</span>
      <textarea class="mp-note-input" rows="2"
        placeholder="z. B. gut durchgebraten · ohne Zwiebeln (Allergie) · extra scharf"
        oninput="mpSetLineNote('${line.lineId}', this.value)">${htmlEscape(line.note || '')}</textarea>
    </div>
  `;
}

// -----------------------------------------------------------------------------
// Konfigurations-Modal (Pop-Up vor dem Warenkorb-Add)
// -----------------------------------------------------------------------------
export function renderDraftModal(draftLine, menu, dietFilter, isOperator) {
  if (!draftLine) return '';
  const line = draftLine;
  const item = findItemFromMenu(menu, line.ref);
  if (!item) return '';
  const meta = item.meta || {};

  // Pflicht-Hinweis im Footer, wenn etwas fehlt.
  const missing = [];
  if (meta.isBurger && !line.bun)                          missing.push('Brötchen');
  if (meta.isBowl && meta.requiresPatty && !line.patty)    missing.push('Patty');
  if (meta.isBowl && meta.requiresDressing && !line.dressing) missing.push('Dressing');

  return `
    <div class="mp-modal-overlay" onclick="mpCancelDraft()">
      <div class="mp-modal" onclick="event.stopPropagation()" role="dialog" aria-modal="true">
        <div class="mp-modal-header">
          <div>
            <div class="mp-modal-title">${htmlEscape(line.name)}</div>
            <div class="mp-modal-subtitle">
              ${item.subcategory ? htmlEscape(item.subcategory) + ' · ' : ''}${fmtEUR(item.price)}
              ${dietTagBadges(item.dietaryTags)}
            </div>
            ${item.description ? `<div class="mp-modal-desc">${htmlEscape(item.description)}</div>` : ''}
          </div>
          <button type="button" class="mp-modal-close"
                  onclick="mpCancelDraft()" aria-label="Schließen">✕</button>
        </div>
        <div class="mp-modal-body">${renderLineConfigBody(line, item, menu, dietFilter)}${isOperator ? renderKitchenNoteBlock(line) : ''}</div>
        ${renderModalFooter(line, item, missing)}
      </div>
    </div>
  `;
}

/** Footer mit Mengen-Stepper, Live-Preis und Action-Buttons.
 *  Eigenständig, damit `softRefresh` ihn separat ersetzen kann ohne
 *  den Body neu zu rendern (verhindert Re-Trigger der Open-Animation). */
export function renderModalFooter(line, item, missing) {
  if (!missing) {
    missing = [];
    const meta = (item && item.meta) || {};
    if (meta.isBurger && !line.bun)                            missing.push('Brötchen');
    if (meta.isBowl && meta.requiresPatty && !line.patty)      missing.push('Patty');
    if (meta.isBowl && meta.requiresDressing && !line.dressing) missing.push('Dressing');
  }
  return `
    <div class="mp-modal-footer">
      <div class="mp-modal-qty">
        <span class="mp-modal-qty-label">Menge</span>
        <div class="mp-qty">
          <button type="button" class="mp-qty-btn" onclick="mpChangeQty('${line.lineId}', -1)" aria-label="Weniger">−</button>
          <span class="mp-qty-val">${line.qty}</span>
          <button type="button" class="mp-qty-btn" onclick="mpChangeQty('${line.lineId}', 1)" aria-label="Mehr">+</button>
        </div>
      </div>
      <div class="mp-modal-total">
        <span class="mp-modal-total-label">Position</span>
        <span class="mp-modal-total-value">${fmtEUR(line.lineTotal)}</span>
      </div>
      <div class="mp-modal-actions">
        <button type="button" class="btn btn-ghost" onclick="mpCancelDraft()">Abbrechen</button>
        <button type="button" class="btn btn-primary"
                onclick="mpConfirmDraft()"
                ${missing.length ? `title="Bitte wähle noch: ${htmlEscape(missing.join(', '))}"` : ''}>
          ${line._isEdit ? 'Speichern' : 'In den Warenkorb'}
          ${missing.length ? `<small style="margin-left:6px; opacity:.85;">(${htmlEscape(missing.join(', '))} fehlt)</small>` : ''}
        </button>
      </div>
    </div>
  `;
}
